import { useState, useEffect } from "react";
import api from "../lib/api";
import { useNavigate } from "react-router-dom";
import { 
  Loader2, User, Phone, MapPin, Calendar, Clock, AlertCircle, 
  Fuel, Bell, Save, CheckCircle, ShieldAlert, Navigation, MapPinOff 
} from "lucide-react";
import { Header } from "../components/header";
import { Footer } from "../components/footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

export default function ProfilePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [daysRemaining, setDaysRemaining] = useState(0);

  // Vendor specific state
  const [vendorData, setVendorData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState("profile"); // profile, cylinders, notifications

  // Cylinder form state
  const [espanolStock, setEspanolStock] = useState(0);
  const [francesaStock, setFrancesaStock] = useState(0);
  const [updatingStock, setUpdatingStock] = useState(false);
  const [stockSuccess, setStockSuccess] = useState("");
  const [stockError, setStockError] = useState("");

  // Point of sale activation state
  const [activatingPOS, setActivatingPOS] = useState(false);
  const [posSuccess, setPosSuccess] = useState("");
  const [posError, setPosError] = useState("");

  // Profile edit form state
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState("");
  const [profileError, setProfileError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    const fetchProfile = async () => {
      try {
        const { data } = await api.get("/api/user/profile");
        if (data.success && data.user) {
          setUser(data.user);
          localStorage.setItem("user", JSON.stringify(data.user));

          setEditName(data.user.name || "");
          setEditPhone(data.user.phone || "");
          setEditAddress(data.user.address || "");

          const trialEnd = new Date(data.user.trial_end_date);
          const now = new Date();
          const diffTime = trialEnd.getTime() - now.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          setDaysRemaining(diffDays);

          if (data.user.role === "vendor") {
            await fetchVendorDataAndNotifications();
          }
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        // Fallback to local storage if API call fails
        const userData = localStorage.getItem("user");
        if (userData) {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          setEditName(parsedUser.name || "");
          setEditPhone(parsedUser.phone || "");
          setEditAddress(parsedUser.address || "");

          const trialEnd = new Date(parsedUser.trial_end_date);
          const now = new Date();
          const diffTime = trialEnd.getTime() - now.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          setDaysRemaining(diffDays);
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchVendorDataAndNotifications = async () => {
      try {
        const [vendorRes, notifRes] = await Promise.all([
          api.get("/api/vendors"),
          api.get("/api/notifications")
        ]);

        if (vendorRes.data && vendorRes.data.length > 0) {
          const v = vendorRes.data[0];
          setVendorData(v);
          setEspanolStock(v.cylinders?.espanol || 0);
          setFrancesaStock(v.cylinders?.francesa || 0);
        }

        if (notifRes.data) {
          setNotifications(notifRes.data);
        }
      } catch (err) {
        console.error("Error fetching vendor data or notifications:", err);
      }
    };

    fetchProfile();
  }, [navigate]);

  const handleUpdateStock = async (e) => {
    e.preventDefault();
    if (!vendorData) return;

    setUpdatingStock(true);
    setStockSuccess("");
    setStockError("");

    try {
      const { data } = await api.put(`/api/vendors/${vendorData.id}`, {
        cylinders: {
          espanol: Number(espanolStock),
          francesa: Number(francesaStock)
        }
      });

      setStockSuccess("¡Stock de bombonas actualizado correctamente!");
      setVendorData(prev => ({
        ...prev,
        cylinders: {
          espanol: data.cylinders.espanol,
          francesa: data.cylinders.francesa
        }
      }));
    } catch (err) {
      console.error("Error updating cylinders:", err);
      setStockError("No se pudo actualizar el stock de bombonas.");
    } finally {
      setUpdatingStock(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setUpdatingProfile(true);
    setProfileSuccess("");
    setProfileError("");

    try {
      // If they are a vendor, we update the vendor entity (which updates the user internally)
      if (user.role === "vendor" && vendorData) {
        const { data } = await api.put(`/api/vendors/${vendorData.id}`, {
          name: editName,
          phone: editPhone,
          address: editAddress
        });

        setProfileSuccess("Perfil de punto de venta actualizado con éxito");
        setVendorData(prev => ({
          ...prev,
          name: data.name,
          phone: data.phone,
          address: data.address
        }));

        setUser(prev => ({
          ...prev,
          name: editName,
          phone: editPhone,
          address: editAddress
        }));
      } else {
        // Normal user profile edit (if supported by api/user/profile PUT or similar, otherwise fallback message)
        // Note: For now we focus on Sales Rep/Vendor management as requested.
        setProfileError("La edición de perfil para usuarios generales no está habilitada.");
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      setProfileError("Ocurrió un error al actualizar los datos.");
    } finally {
      setUpdatingProfile(false);
    }
  };

  const handleActivatePointOfSale = () => {
    if (!vendorData) return;
    setActivatingPOS(true);
    setPosSuccess("");
    setPosError("");

    if (!navigator.geolocation) {
      setPosError("Tu dispositivo no soporta geolocalización. Intenta desde el navegador de tu teléfono móvil.");
      setActivatingPOS(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const { data } = await api.post(`/api/vendors/${vendorData.id}/activate-pos`, {
            latitude,
            longitude
          });

          setPosSuccess(`¡Punto de venta activado! Coordenadas registradas: ${latitude.toFixed(5)}, ${longitude.toFixed(5)}`);
          setVendorData(prev => ({
            ...prev,
            position: data.position,
            pointOfSaleActivated: data.pointOfSaleActivated
          }));
        } catch (err) {
          console.error("Error activating POS:", err);
          const msg = err.response?.data?.error || "No se pudo activar el punto de venta. Inténtalo de nuevo.";
          setPosError(msg);
        } finally {
          setActivatingPOS(false);
        }
      },
      (geoErr) => {
        const messages = {
          1: "Permiso de ubicación denegado. Activa la ubicación en tu navegador e inténtalo de nuevo.",
          2: "No se pudo obtener tu ubicación. Asegúrate de tener señal GPS.",
          3: "La solicitud de ubicación tardó demasiado. Inténtalo de nuevo."
        };
        setPosError(messages[geoErr.code] || "Error desconocido al obtener la ubicación.");
        setActivatingPOS(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleMarkNotificationAsRead = async (notifId) => {
    try {
      await api.put(`/api/notifications/${notifId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notifId ? { ...n, read: true } : n)
      );
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#0288d0]/5 to-white">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-10 w-10 animate-spin text-[#0288d0]" />
          <p className="text-muted-foreground">Cargando perfil...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#0288d0]/5 to-white">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No se pudo cargar la información del usuario. Por favor, inicia sesión nuevamente.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "Africa/Malabo",
    });
  };

  const isVendor = user.role === "vendor";

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 bg-gradient-to-b from-[#0288d0]/5 to-white py-8 md:py-12">
        <div className="container mx-auto px-4 max-w-5xl">
          
          {/* Header Title */}
          <div className="mb-8 text-center md:text-left flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#0288d0] mb-2">
                {isVendor ? "Portal de Vendedor" : "Mi Perfil"}
              </h1>
              <p className="text-muted-foreground">
                {isVendor 
                  ? "Gestiona tus bombonas, actualiza los datos del punto de venta y revisa alertas" 
                  : "Gestiona tu información personal y estado de cuenta"}
              </p>
            </div>
            {isVendor && vendorData && (
              <Badge className={vendorData.isActive ? "bg-green-600 hover:bg-green-700 text-white py-1.5 px-3 text-sm" : "bg-red-600 hover:bg-red-700 text-white py-1.5 px-3 text-sm"}>
                {vendorData.isActive ? "Contrato Activo" : "Contrato Inactivo"}
              </Badge>
            )}
          </div>

          {/* Account/Trial status Alerts */}
          {!isVendor && (
            <>
              {daysRemaining > 0 && daysRemaining <= 7 && (
                <Alert className="mb-6 border-orange-500 bg-orange-50">
                  <Clock className="h-5 w-5 text-orange-600" />
                  <AlertDescription className="text-orange-700 font-medium">
                    Tu período de prueba termina en <strong>{daysRemaining} {daysRemaining === 1 ? "día" : "días"}</strong>. ¡No pierdas acceso!
                  </AlertDescription>
                </Alert>
              )}

              {daysRemaining > 7 && daysRemaining <= 30 && (
                <Alert className="mb-6 border-[#0288d0] bg-[#0288d0]/5">
                  <Clock className="h-5 w-5 text-[#0288d0]" />
                  <AlertDescription className="text-[#0288d0]">
                    Te quedan <strong>{daysRemaining} días</strong> de prueba gratuita
                  </AlertDescription>
                </Alert>
              )}

              {daysRemaining <= 0 && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-5 w-5" />
                  <AlertDescription className="font-medium">
                    Tu período de prueba ha expirado. 
                    <span className="block mt-1 text-sm">
                      Contacta con <a href="tel:+240222123456" className="underline">+240 222 123 456</a> para activar tu plan.
                    </span>
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          {/* Vendor Tabs System */}
          {isVendor && (
            <div className="flex border-b border-gray-200 mb-8 overflow-x-auto gap-2">
              <button
                onClick={() => setActiveTab("profile")}
                className={`py-3 px-4 font-semibold text-sm transition-all border-b-2 whitespace-nowrap ${
                  activeTab === "profile" 
                    ? "border-[#0288d0] text-[#0288d0]" 
                    : "border-transparent text-gray-500 hover:text-[#0288d0]"
                }`}
              >
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Mi Cuenta
                </div>
              </button>
              <button
                onClick={() => setActiveTab("cylinders")}
                className={`py-3 px-4 font-semibold text-sm transition-all border-b-2 whitespace-nowrap ${
                  activeTab === "cylinders" 
                    ? "border-[#0288d0] text-[#0288d0]" 
                    : "border-transparent text-gray-500 hover:text-[#0288d0]"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Fuel className="w-4 h-4" />
                  Gestionar Bombonas
                </div>
              </button>
              <button
                onClick={() => setActiveTab("notifications")}
                className={`py-3 px-4 font-semibold text-sm transition-all border-b-2 whitespace-nowrap relative ${
                  activeTab === "notifications" 
                    ? "border-[#0288d0] text-[#0288d0]" 
                    : "border-transparent text-gray-500 hover:text-[#0288d0]"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  Notificaciones
                  {notifications.filter(n => !n.read).length > 0 && (
                    <Badge variant="destructive" className="ml-1 px-1.5 py-0.5 text-xs rounded-full">
                      {notifications.filter(n => !n.read).length}
                    </Badge>
                  )}
                </div>
              </button>
            </div>
          )}

          {/* Render regular user view or active tab for vendor */}
          {!isVendor ? (
            /* REGULAR USER VIEW */
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              <Card className="shadow-sm hover:shadow-md transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#0288d0]">
                    <User className="h-5 w-5" />
                    Información Personal
                  </CardTitle>
                  <CardDescription>Tus datos registrados en GeoGas</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <Label className="text-muted-foreground">Nombre Completo</Label>
                    <p className="text-lg font-medium text-right">
                      {user.name} {user.surname}
                    </p>
                  </div>

                  <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <Label className="text-muted-foreground">Usuario</Label>
                    <p className="text-lg font-medium text-right">@{user.username}</p>
                  </div>

                  <div className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0">
                    <Phone className="h-4 w-4 text-muted-foreground mt-1" />
                    <div className="flex-1">
                      <Label className="text-muted-foreground">Teléfono</Label>
                      <p className="text-lg font-medium">{user.phone}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 py-2">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                    <div className="flex-1">
                      <Label className="text-muted-foreground">Dirección</Label>
                      <p className="text-lg font-medium">{user.address}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-sm hover:shadow-md transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#0288d0]">
                    <Calendar className="h-5 w-5" />
                    Estado de la Cuenta
                  </CardTitle>
                  <CardDescription>Resumen de tu suscripción</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <Label className="text-muted-foreground">Fecha de Registro</Label>
                    <p className="text-lg font-medium text-right">{formatDate(user.created_at)}</p>
                  </div>

                  <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <Label className="text-muted-foreground">Fin del Trial</Label>
                    <p className="text-lg font-medium text-right">{formatDate(user.trial_end_date)}</p>
                  </div>

                  <div className="pt-3">
                    <Label className="text-muted-foreground">Estado Actual</Label>
                    <div className="mt-2 p-3 rounded-lg bg-muted/50 text-center">
                      {daysRemaining > 0 ? (
                        <p className="text-xl font-bold text-green-600">
                          Activo
                          <span className="block text-sm font-normal text-green-700 mt-1">
                            {daysRemaining} {daysRemaining === 1 ? "día" : "días"} restantes
                          </span>
                        </p>
                      ) : (
                        <p className="text-xl font-bold text-red-600">
                          Expirado
                          <span className="block text-sm font-normal text-red-700 mt-1">
                            Contacta con soporte
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            /* VENDOR VIEW TABS CONTENTS */
            <div>
              {/* TAB 1: PROFILE MANAGEMENT */}
              {activeTab === "profile" && (
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Account / Contract status */}
                  <Card className="shadow-sm md:col-span-1 h-fit">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-[#0288d0] text-lg">
                        <Calendar className="h-5 w-5" />
                        Contrato y Estado
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm">
                      <div>
                        <Label className="text-muted-foreground text-xs block mb-1">Nombre de Usuario</Label>
                        <p className="font-semibold text-base">@{user.username}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs block mb-1">Inicio del Contrato</Label>
                        <p className="font-medium text-base">{formatDate(vendorData?.contractStartDate)}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs block mb-1">Fin del Contrato</Label>
                        <p className={`font-semibold text-base ${
                          vendorData?.contractEndDate && new Date(vendorData.contractEndDate) < new Date() 
                            ? "text-red-600" 
                            : "text-green-600"
                        }`}>
                          {formatDate(vendorData?.contractEndDate)}
                        </p>
                      </div>
                      {vendorData?.contractEndDate && new Date(vendorData.contractEndDate) < new Date() && (
                        <Alert variant="destructive" className="p-3">
                          <ShieldAlert className="w-4 h-4" />
                          <AlertDescription className="text-xs">
                            Tu contrato ha expirado. Por favor, solicita una renovación al administrador.
                          </AlertDescription>
                        </Alert>
                      )}

                      {/* Point of Sale Activation */}
                      <div className="pt-2 border-t border-border/50">
                        <Label className="text-muted-foreground text-xs block mb-3">Punto de Venta</Label>
                        {vendorData?.pointOfSaleActivated ? (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-green-700 font-semibold text-sm">
                              <CheckCircle className="w-4 h-4" />
                              Punto de venta activo
                            </div>
                            {vendorData?.position?.lat && (
                              <p className="text-xs text-muted-foreground font-mono">
                                {vendorData.position.lat.toFixed(5)}, {vendorData.position.lng.toFixed(5)}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-orange-600 text-sm mb-2">
                              <MapPinOff className="w-4 h-4" />
                              <span>Sin ubicación registrada</span>
                            </div>
                            {posSuccess && (
                              <Alert className="p-2 border-green-500 bg-green-50 text-green-700">
                                <CheckCircle className="h-3 w-3 text-green-600" />
                                <AlertDescription className="text-xs">{posSuccess}</AlertDescription>
                              </Alert>
                            )}
                            {posError && (
                              <Alert variant="destructive" className="p-2">
                                <AlertCircle className="h-3 w-3" />
                                <AlertDescription className="text-xs">{posError}</AlertDescription>
                              </Alert>
                            )}
                            <Button
                              id="activate-pos-btn"
                              type="button"
                              onClick={handleActivatePointOfSale}
                              disabled={activatingPOS}
                              className="w-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold"
                            >
                              {activatingPOS ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Obteniendo ubicación...
                                </>
                              ) : (
                                <>
                                  <Navigation className="w-4 h-4 mr-2" />
                                  Activar Punto de Venta
                                </>
                              )}
                            </Button>
                            <p className="text-xs text-muted-foreground text-center leading-tight">
                              Pulsa desde la ubicación de tu establecimiento. Se registrarán tus coordenadas GPS.
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Edit profile form */}
                  <Card className="shadow-sm md:col-span-2">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-[#0288d0] text-lg">
                        <User className="h-5 w-5" />
                        Datos del Punto de Venta
                      </CardTitle>
                      <CardDescription>Actualiza el nombre público, teléfono de contacto y dirección física</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {profileSuccess && (
                        <Alert className="mb-4 border-green-500 bg-green-50 text-green-700">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <AlertDescription>{profileSuccess}</AlertDescription>
                        </Alert>
                      )}
                      {profileError && (
                        <Alert variant="destructive" className="mb-4">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>{profileError}</AlertDescription>
                        </Alert>
                      )}
                      <form onSubmit={handleUpdateProfile} className="space-y-4">
                        <div className="space-y-1">
                          <Label htmlFor="vendorName">Nombre del Establecimiento / Vendedor</Label>
                          <Input 
                            id="vendorName"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            required
                            placeholder="Ej. Gas Malabo Express"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="vendorPhone">Teléfono de Atención al Cliente</Label>
                          <Input 
                            id="vendorPhone"
                            value={editPhone}
                            onChange={(e) => setEditPhone(e.target.value)}
                            required
                            placeholder="Ej. +240 222 123 456"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="vendorAddress">Dirección / Barrio</Label>
                          <Input 
                            id="vendorAddress"
                            value={editAddress}
                            onChange={(e) => setEditAddress(e.target.value)}
                            required
                            placeholder="Ej. Barrio Ela Nguema, Malabo"
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="bg-[#0288d0] hover:bg-[#0288d0]/90 text-white w-full sm:w-auto"
                          disabled={updatingProfile}
                        >
                          {updatingProfile ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Guardando...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              Guardar Cambios
                            </>
                          )}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* TAB 2: CYLINDERS STOCK MANAGEMENT */}
              {activeTab === "cylinders" && (
                <Card className="shadow-sm max-w-2xl mx-auto">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#0288d0] text-lg">
                      <Fuel className="h-5 w-5" />
                      Actualizar Stock de Bombonas
                    </CardTitle>
                    <CardDescription>
                      Introduce el stock actual disponible en tu punto de venta. Esta información se muestra en tiempo real a los clientes en el mapa.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {stockSuccess && (
                      <Alert className="mb-4 border-green-500 bg-green-50 text-green-700">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <AlertDescription>{stockSuccess}</AlertDescription>
                      </Alert>
                    )}
                    {stockError && (
                      <Alert variant="destructive" className="mb-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{stockError}</AlertDescription>
                      </Alert>
                    )}
                    
                    <form onSubmit={handleUpdateStock} className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="p-4 border rounded-lg bg-gray-50 flex flex-col justify-between">
                          <div>
                            <span className="text-sm font-semibold text-green-800 block mb-1">Bombona Española (12.5kg)</span>
                            <span className="text-xs text-muted-foreground">Unidades disponibles actualmente</span>
                          </div>
                          <div className="mt-4">
                            <Input 
                              type="number"
                              min="0"
                              value={espanolStock}
                              onChange={(e) => setEspanolStock(Math.max(0, parseInt(e.target.value) || 0))}
                              className="text-lg font-bold text-center h-12"
                            />
                          </div>
                        </div>

                        <div className="p-4 border rounded-lg bg-gray-50 flex flex-col justify-between">
                          <div>
                            <span className="text-sm font-semibold text-blue-800 block mb-1">Bombona Francesa (13kg)</span>
                            <span className="text-xs text-muted-foreground">Unidades disponibles actualmente</span>
                          </div>
                          <div className="mt-4">
                            <Input 
                              type="number"
                              min="0"
                              value={francesaStock}
                              onChange={(e) => setFrancesaStock(Math.max(0, parseInt(e.target.value) || 0))}
                              className="text-lg font-bold text-center h-12"
                            />
                          </div>
                        </div>
                      </div>

                      <Button 
                        type="submit" 
                        className="bg-[#0288d0] hover:bg-[#0288d0]/90 text-white w-full h-12 text-base font-semibold"
                        disabled={updatingStock}
                      >
                        {updatingStock ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Actualizando Stock...
                          </>
                        ) : (
                          <>
                            <Save className="w-5 h-5 mr-2" />
                            Confirmar Stock de Bombonas
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* TAB 3: NOTIFICATIONS VIEW */}
              {activeTab === "notifications" && (
                <Card className="shadow-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#0288d0] text-lg">
                      <Bell className="h-5 w-5" />
                      Historial de Alertas y Notificaciones
                    </CardTitle>
                    <CardDescription>
                      Revisa las alertas automáticas de stock bajo o vencimiento de contrato emitidas para tu establecimiento.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {notifications.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Bell className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p className="font-medium text-lg">No tienes notificaciones</p>
                        <p className="text-sm">Te avisaremos cuando haya novedades importantes sobre tu stock o contrato.</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {notifications.map((notif) => (
                          <div 
                            key={notif.id} 
                            className={`py-4 flex items-start justify-between gap-4 first:pt-0 last:pb-0 ${
                              !notif.read ? "bg-blue-50/40 -mx-4 px-4 rounded" : ""
                            }`}
                          >
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Badge className={
                                  notif.type === "stock" 
                                    ? "bg-orange-100 text-orange-800 hover:bg-orange-100" 
                                    : "bg-red-100 text-red-800 hover:bg-red-100"
                                }>
                                  {notif.type === "stock" ? "Alerta Stock" : "Contrato"}
                                </Badge>
                                {!notif.read && (
                                  <Badge className="bg-blue-600 text-white hover:bg-blue-600">Nuevo</Badge>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {new Date(notif.timestamp).toLocaleDateString("es-ES", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit"
                                  })}
                                </span>
                              </div>
                              <p className="text-sm font-medium text-gray-800">{notif.message}</p>
                            </div>
                            
                            {!notif.read && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-xs shrink-0"
                                onClick={() => handleMarkNotificationAsRead(notif.id)}
                              >
                                Marcar como leída
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Regular Support CTA at bottom if subscription expired */}
          {!isVendor && daysRemaining <= 0 && (
            <div className="mt-8 text-center">
              <a
                href="tel:+240222123456"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#0288d0] text-white font-medium rounded-lg hover:bg-[#0288d0]/90 transition-colors shadow"
              >
                <Phone className="h-5 w-5" />
                Llamar a Soporte
              </a>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}